import { MRT_ColumnDef } from "material-react-table";
import type React from "react";
import { TFunction } from "i18next";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";
import { FleetManagementRow } from "./types/FleetManagement-row.types";
import { FleetAttachment, FleetFuelType, FleetStatus } from "./types/FleetManagement.types";
import { LocationInventory } from "../../features/FloorManager/typings/location-inventory";
import { BookingProperty } from "../FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";
import {
  editPermissionSelect,
  editTableItemMultiImagesComp,
  renderPermissionCell,
  renderSimpleCellName,
  renderTableItemMultiImages
} from "../Common/ListTablePartial/ListTablePartial.partial";
import {
  editAttachments,
  renderBlockedUsersCell,
  renderFuelCell,
  renderLogbookCell,
  renderOccupiedBookingCell,
  renderOdometerCell,
  renderStatusCell
} from "./FleetManagementTable.partial";
import {
  editFleetProperties,
  renderFleetAttachmentsCell,
  renderFleetPropertiesCell
} from "./EventDetailDialog/partial/FleetManagementEventDetailTable.partial";
import {
  checkFleetItemVal,
  compareFleetStatusThenLocation
} from "./functions/FleetManagementTable.functions";
import type { CheckFleetValType } from "./functions/FleetManagementTable.functions";
import { FileObject } from "../../features/FileDropzone/typings/FileObject.type";
import { NumberFormatCustom } from "../../features/Admin/finance-administration.component";

export type FleetTableColumnsContext = {
  isEditable: boolean;
  t: TFunction;
  language: string;
  dateFormat: string;
  timezone: string;

  // Validation
  validationErrors: Record<string, string | undefined>;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>;

  // Reference data
  locations: LocationInventory[] | undefined;
  remoteBookingProperty: BookingProperty[] | undefined;
  // use loose typing here to avoid over-constraining remote data shapes
  remoteCompanies: any[] | undefined;
  remoteDepartments: any[] | undefined;
  remoteAllUsers: any[] | undefined;

  // Draft state
  attachmentDraft: FleetAttachment[];
  setAttachmentDraft: React.Dispatch<React.SetStateAction<FleetAttachment[]>>;
  propertiesDraft: BookingProperty[];
  setPropertiesDraft: React.Dispatch<React.SetStateAction<BookingProperty[]>>;

  // Images
  imageFiles: FileObject[];
  setImageFiles: React.Dispatch<React.SetStateAction<FileObject[]>>;
  deleteSelectedImage: (picturePath: string, rowOriginal: FleetManagementRow) => void;
  setShowBiggerImages: (paths: string[] | null) => void;

  // Attachments
  deleteFleetAttachment?: (vehicleId: number, attachmentId: number) => void | Promise<void>;

  // Dialog setters
  setVehicleIdForBlockedUsersDialog: (id: number | null) => void;
  setVehicleNameForBlockedUsersDialog: (name: string) => void;
  setLogbookDialog: (
    dialog: { vehicleId: number; vehicleName: string; licensePlate?: string } | null
  ) => void;

  // Row selection for event dialog
  setSelectedRow: (row: FleetManagementRow | null) => void;

  /** Booking mode: vehicle favorites */
  vehicleFavoriteIds?: ReadonlySet<number>;
  onToggleVehicleFavorite?: (
    vehicleInventoryId: number,
    currentlyFavorite: boolean
  ) => void | Promise<void>;
  vehicleFavoriteActionPending?: boolean;
};

const createValidatedTextFieldProps = (
  field: CheckFleetValType,
  ctx: Pick<FleetTableColumnsContext, "validationErrors" | "setValidationErrors">,
  options?: { required?: boolean }
) => {
  const { validationErrors, setValidationErrors } = ctx;
  const required = options?.required !== false;

  return {
    onFocus: () =>
      setValidationErrors({
        ...validationErrors,
        [field]: undefined
      }),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      checkFleetItemVal(e.currentTarget.value, field, validationErrors, setValidationErrors);
    },
    helperText: validationErrors?.[field],
    required,
    error: !!validationErrors?.[field]
  };
};

function compactFleetColumnDefs(
  columns: Array<MRT_ColumnDef<FleetManagementRow> | null | undefined>
): MRT_ColumnDef<FleetManagementRow>[] {
  return columns.filter((c): c is MRT_ColumnDef<FleetManagementRow> => c != null);
}

type FleetVehicleFavoriteCellProps = Readonly<{
  vehicleId: number;
  isFavorite: boolean;
  t: TFunction;
  disabled?: boolean;
  onToggle?: (vehicleInventoryId: number, currentlyFavorite: boolean) => void | Promise<void>;
}>;

function FleetVehicleFavoriteCell({
  vehicleId,
  isFavorite,
  t,
  disabled,
  onToggle
}: FleetVehicleFavoriteCellProps) {
  const label = isFavorite
    ? t("_bookingFavorites_removeFromFavorites")
    : t("_bookingFavorites_addToFavorites");
  const icon = isFavorite ? <Star color="warning" /> : <StarBorder />;
  return (
    <Tooltip title={<Typography>{label}</Typography>}>
      <span>
        <IconButton
          size="small"
          data-testid={`fleet-vehicle-favorite-${vehicleId}`}
          onClick={e => {
            e.stopPropagation();
            onToggle?.(vehicleId, isFavorite);
          }}
          disabled={disabled}
          aria-label={label}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function resolveCompanyCellDisplayName(
  original: FleetManagementRow,
  remoteCompanies: FleetTableColumnsContext["remoteCompanies"] | null
): string | undefined {
  if (original.companyName != null) {
    return original.companyName;
  }
  if (remoteCompanies == null || original.companyId == null) {
    return undefined;
  }
  return remoteCompanies.find(c => c.id === original.companyId)?.name;
}

export function buildFleetBaseColumns(
  ctx: FleetTableColumnsContext
): MRT_ColumnDef<FleetManagementRow>[] {
  const {
    isEditable,
    t,
    language,
    validationErrors,
    setValidationErrors,
    locations,
    remoteBookingProperty,
    propertiesDraft,
    setPropertiesDraft,
    imageFiles,
    setImageFiles,
    deleteSelectedImage,
    setShowBiggerImages
  } = ctx;

  const odometerColumn: MRT_ColumnDef<FleetManagementRow> | null = isEditable
    ? ({
        accessorKey: "odometer",
        header: t("Odometer"),
        enableEditing: true,
        size: 150,
        Cell: ({ cell }) =>
          renderOdometerCell({
            value: cell.getValue<number | null>(),
            language
          }),
        muiEditTextFieldProps: {
          type: "number",
          inputProps: { min: 1 },
          ...createValidatedTextFieldProps("odometer", {
            validationErrors,
            setValidationErrors
          })
        }
      } as MRT_ColumnDef<FleetManagementRow>)
    : null;

  return [
    {
      accessorKey: "picturePaths",
      Edit: ({ row }) =>
        editTableItemMultiImagesComp({
          title: t("Upload vehicle images"),
          rowOriginal: row.original,
          imageFiles,
          setImageFiles,
          handleDelete: picturePath => deleteSelectedImage(picturePath, row.original)
        }),
      Cell: ({ cell }) =>
        renderTableItemMultiImages(
          cell.row.original.picturePaths,
          "vehicle-image",
          100,
          setShowBiggerImages
        ),
      size: 100,
      header: t("Image"),
      enableEditing: true,
      enableColumnFilter: false,
      enableSorting: false
    },
    {
      accessorKey: "name",
      header: t("Name"),
      enableEditing: true,
      size: 200,
      muiEditTextFieldProps: createValidatedTextFieldProps("name", {
        validationErrors,
        setValidationErrors
      })
    },
    {
      size: 200,
      enableEditing: true,
      accessorKey: "description",
      header: t("Description"),
      muiEditTextFieldProps: createValidatedTextFieldProps(
        "description",
        {
          validationErrors,
          setValidationErrors
        },
        { required: false }
      )
    },
    {
      accessorKey: "licensePlate",
      header: t("LicensePlate"),
      enableEditing: true,
      size: 150,
      muiEditTextFieldProps: createValidatedTextFieldProps("licensePlate", {
        validationErrors,
        setValidationErrors
      })
    },
    {
      accessorKey: "brandModel",
      header: t("Brand Model"),
      enableEditing: true,
      size: 150,
      muiEditTextFieldProps: createValidatedTextFieldProps("brandModel", {
        validationErrors,
        setValidationErrors
      })
    },
    {
      accessorKey: "vehicleType",
      header: t("Vehicle Type"),
      enableEditing: true,
      size: 150
    },
    {
      accessorKey: "fuel",
      header: t("Fuel Type"),
      enableEditing: true,
      size: 150,
      Cell: ({ row: { original } }) => renderFuelCell(original),
      filterSelectOptions: Object.entries(FleetFuelType).map(fuel => ({
        label: t(fuel[1]),
        value: fuel[1],
        key: fuel[0]
      })),
      filterVariant: "multi-select",
      filterFn: "arrIncludesSome",
      editVariant: "select",
      editSelectOptions: () =>
        Object.entries(FleetFuelType).map(fuel => ({
          label: t(fuel[1]),
          value: fuel[1],
          key: fuel[0]
        })),
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors.fuel,
        helperText: validationErrors.fuel,
        onChange: (event: any) => {
          // when user selects a fuel value, clear any previous validation error
          /* istanbul ignore next */
          if (event?.target?.value && validationErrors.fuel) {
            setValidationErrors(prev => ({
              ...prev,
              fuel: undefined
            }));
          }
        }
      }
    },
    {
      accessorKey: "reach",
      header: t("Reach"),
      enableEditing: true,
      size: 150,
      muiEditTextFieldProps: {
        type: "number",
        inputProps: { min: 0 },
        ...createValidatedTextFieldProps(
          "reach",
          {
            validationErrors,
            setValidationErrors
          },
          { required: false }
        )
      }
    },
    {
      accessorKey: "numSeats",
      header: t("Number of Seats"),
      enableEditing: true,
      size: 150,
      muiEditTextFieldProps: {
        type: "number",
        inputProps: { min: 1 },
        ...createValidatedTextFieldProps("numSeats", {
          validationErrors,
          setValidationErrors
        })
      }
    },
    ...compactFleetColumnDefs([odometerColumn]),
    {
      accessorKey: "properties",
      header: t("Properties"),
      enableEditing: true,
      size: 150,
      Cell: ({ row: { original } }) => renderFleetPropertiesCell(original.properties),
      Edit: ({ row }) =>
        editFleetProperties(
          row.original,
          remoteBookingProperty,
          propertiesDraft,
          setPropertiesDraft
        )
    },
    {
      accessorKey: "location.name",
      header: t("Location"),
      Cell: ({ row: { original } }) => renderSimpleCellName(original.location?.name),
      editSelectOptions: ({ row }) => {
        const locNull = { name: t("No location"), value: null };
        const arrLoc = locations ?? [
          { id: row.original.location?.id, name: row.original.location?.name }
        ];
        const locOpts = [locNull, ...arrLoc];
        return locOpts.map(loc => ({ label: loc.name, value: loc.name, key: loc.name }));
      },
      size: 200,
      editVariant: "select"
    },
    {
      accessorKey: "status",
      header: t("Status"),
      enableEditing: false,
      size: 180,
      sortingFn: (rowA, rowB) => compareFleetStatusThenLocation(rowA.original, rowB.original),
      filterSelectOptions: Object.entries(FleetStatus).map(stat => ({
        label: t(stat[1]),
        value: stat[1],
        key: stat[0]
      })),
      filterVariant: "multi-select",
      filterFn: "arrIncludesSome",
      Cell: ({ row: { original } }) => renderStatusCell(original)
    }
  ];
}

function buildFleetAdminOnlyColumns(
  ctx: FleetTableColumnsContext
): MRT_ColumnDef<FleetManagementRow>[] {
  if (!ctx.isEditable) {
    return [];
  }

  const { t, validationErrors, setValidationErrors, remoteCompanies } = ctx;

  return [
    {
      accessorKey: "companyId",
      header: t("Company"),
      enableEditing: true,
      size: 200,
      Cell: ({ row: { original } }) =>
        renderSimpleCellName(resolveCompanyCellDisplayName(original, remoteCompanies)),
      editVariant: "select",
      editSelectOptions: () => {
        const opts = (remoteCompanies ?? []).map(c => ({
          label: c.name ?? `${t("Company")} ${c.id}`,
          value: c.id,
          key: String(c.id)
        }));
        return opts;
      },
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors.companyId,
        helperText: validationErrors.companyId,
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          /* istanbul ignore next */
          if (event?.target?.value && validationErrors.companyId) {
            setValidationErrors(prev => ({ ...prev, companyId: undefined }));
          }
        }
      }
    },
    {
      accessorKey: "pricePerHour",
      header: t("Price (h)"),
      enableEditing: true,
      size: 140,
      muiEditTextFieldProps: () => ({
        InputProps: {
          // use locale-aware decimal input (e.g. 5,1 in DE) via react-number-format
          inputComponent: NumberFormatCustom as unknown as React.ElementType
        },
        InputLabelProps: { shrink: true }
      })
    } as MRT_ColumnDef<FleetManagementRow>,
    {
      accessorKey: "bufferMinutes",
      header: t("BufferTime"),
      enableEditing: true,
      size: 140,
      muiEditTextFieldProps: () => ({
        InputProps: {
          // use locale-aware decimal input (e.g. 5,1 in DE) via react-number-format
          inputComponent: NumberFormatCustom as unknown as React.ElementType
        },
        InputLabelProps: { shrink: true }
      })
    } as MRT_ColumnDef<FleetManagementRow>,
    {
      accessorKey: "adminNotes",
      header: t("Admin Notes"),
      enableEditing: true,
      size: 250,
      muiEditTextFieldProps: {
        multiline: true,
        minRows: 1,
        maxRows: 4,
        placeholder: t("Internal notes visible only to fleet admins"),
        InputLabelProps: { shrink: true }
      }
    } as MRT_ColumnDef<FleetManagementRow>,
    {
      accessorKey: "userNotes",
      header: t("User Notes"),
      enableEditing: true,
      size: 250,
      muiEditTextFieldProps: {
        multiline: true,
        minRows: 1,
        maxRows: 4,
        placeholder: t("Notes visible to users who can book this vehicle"),
        InputLabelProps: { shrink: true }
      }
    } as MRT_ColumnDef<FleetManagementRow>
  ];
}

type FleetLayoutColumns = {
  favoriteVehicleColumn: MRT_ColumnDef<FleetManagementRow> | null;
  showEventColumn: MRT_ColumnDef<FleetManagementRow> | null;
  userNotesReadOnlyColumn: MRT_ColumnDef<FleetManagementRow> | null;
  permissionColumn: MRT_ColumnDef<FleetManagementRow> | null;
  blockedUsersColumn: MRT_ColumnDef<FleetManagementRow> | null;
  logbookColumn: MRT_ColumnDef<FleetManagementRow> | null;
  showBookingColumn: MRT_ColumnDef<FleetManagementRow> | null;
};

function buildFleetLayoutColumns(ctx: FleetTableColumnsContext): FleetLayoutColumns {
  const {
    isEditable,
    t,
    timezone,
    remoteCompanies,
    remoteDepartments,
    remoteAllUsers,
    setVehicleIdForBlockedUsersDialog,
    setVehicleNameForBlockedUsersDialog,
    setLogbookDialog,
    setSelectedRow,
    vehicleFavoriteIds,
    onToggleVehicleFavorite,
    vehicleFavoriteActionPending
  } = ctx;

  const renderOccupiedBookingForRow = ({
    row: { original }
  }: {
    row: { original: FleetManagementRow };
  }) =>
    renderOccupiedBookingCell({
      params: original,
      isEditable,
      timezone,
      setSelectRow: setSelectedRow
    });

  if (isEditable) {
    return {
      favoriteVehicleColumn: null,
      showEventColumn: {
        accessorKey: "occupiedBooking",
        header: "",
        size: 30,
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: renderOccupiedBookingForRow
      },
      userNotesReadOnlyColumn: null,
      permissionColumn: {
        accessorKey: "fleetPermissions",
        header: t("Permissions"),
        size: 200,
        editVariant: "select",
        enableEditing: true,
        Edit: props =>
          editPermissionSelect({
            permRow: props.row.original,
            remoteCompanies,
            remoteDepartments,
            remoteAllUsers
          }),
        Cell: ({ row: { original } }) =>
          renderPermissionCell(original, remoteCompanies, remoteDepartments, remoteAllUsers)
      },
      blockedUsersColumn: {
        accessorKey: "blockedUsers",
        header: t("Blocked Users"),
        size: 140,
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row: { original } }) =>
          renderBlockedUsersCell(original, (vehicleId, vehicleName) => {
            setVehicleIdForBlockedUsersDialog(vehicleId);
            setVehicleNameForBlockedUsersDialog(vehicleName);
          })
      },
      logbookColumn: {
        accessorKey: "id",
        id: "logbook",
        header: t("Logbook"),
        size: 100,
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row: { original } }) =>
          renderLogbookCell(original, (vehicleId, vehicleName, licensePlate) => {
            setLogbookDialog({ vehicleId, vehicleName, licensePlate });
          })
      },
      showBookingColumn: null
    };
  }

  return {
    favoriteVehicleColumn: {
      id: "vehicleFavorite",
      header: "",
      size: 52,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <FleetVehicleFavoriteCell
          vehicleId={row.original.id}
          isFavorite={vehicleFavoriteIds?.has(row.original.id) ?? false}
          t={t}
          disabled={vehicleFavoriteActionPending}
          onToggle={onToggleVehicleFavorite}
        />
      )
    },
    showEventColumn: null,
    userNotesReadOnlyColumn: {
      accessorKey: "userNotes",
      header: t("Notes"),
      size: 250,
      enableEditing: false
    },
    permissionColumn: null,
    blockedUsersColumn: null,
    logbookColumn: null,
    showBookingColumn: {
      accessorKey: "occupiedBooking",
      header: t("Bookings"),
      size: 30,
      enableEditing: false,
      Cell: renderOccupiedBookingForRow
    }
  };
}

function buildFleetAttachmentsColumn(
  ctx: FleetTableColumnsContext
): MRT_ColumnDef<FleetManagementRow> {
  const { t, isEditable, attachmentDraft, setAttachmentDraft, deleteFleetAttachment } = ctx;

  return {
    accessorKey: "attachments",
    header: t("Attachments"),
    enableEditing: true,
    enableColumnFilter: false,
    enableSorting: false,
    size: 160,
    Edit: ({ row }) =>
      editAttachments(row.original, attachmentDraft, setAttachmentDraft, deleteFleetAttachment),
    Cell: ({ row: { original } }) => renderFleetAttachmentsCell(original, t, isEditable)
  };
}

export const getFleetTableColumns = (
  ctx: FleetTableColumnsContext
): MRT_ColumnDef<FleetManagementRow>[] => {
  const baseColumns = buildFleetBaseColumns(ctx);
  const adminOnlyColumns = buildFleetAdminOnlyColumns(ctx);
  const layout = buildFleetLayoutColumns(ctx);
  const attachmentsColumn = buildFleetAttachmentsColumn(ctx);

  return [
    ...compactFleetColumnDefs([layout.favoriteVehicleColumn, layout.showEventColumn]),
    ...baseColumns,
    ...adminOnlyColumns,
    ...compactFleetColumnDefs([
      layout.userNotesReadOnlyColumn,
      layout.permissionColumn,
      layout.blockedUsersColumn,
      layout.logbookColumn,
      layout.showBookingColumn
    ]),
    attachmentsColumn
  ];
};
